/**
 * Centralized Logger - Works on both client and server
 * Client: Sends to API endpoint
 * Server: Logs to console (route handlers shouldn't import client-only modules)
 *
 * Rate limiting and circuit breaker to prevent infinite loops and resource exhaustion.
 */

import { LOG_ENDPOINT_MAX_BODY_BYTES } from '@/lib/logEndpointLimits';

const isBrowser = typeof window !== 'undefined';

/**
 * CAUSE-B F4: one id per page load (a tab, or a fresh load in it), shipped on every entry beside the client
 * `timestamp`, so /api/log console lines from one browser can be told apart and tied together.
 */
const TAB_ID = (() => {
  const random = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID().replace(/-/g, '').slice(0, 12)
    : Math.random().toString(36).slice(2, 14).padEnd(12, '0');
  return `tab-${random}`;
})();
const isProductionBuild = process.env.NODE_ENV === 'production';

function parseBooleanFlag(rawValue: string | undefined): boolean | null {
  if (rawValue === undefined) return null;
  const normalized = rawValue.trim().toLowerCase();
  if (['true', '1', 'yes', 'on'].includes(normalized)) return true;
  if (['false', '0', 'no', 'off'].includes(normalized)) return false;
  return null;
}

function getBrowserRuntimeFlag(flagName: string): boolean | null {
  if (!isBrowser) return null;

  const runtimeValue = window.__RUNTIME_CONFIG__?.[flagName];
  const parsedRuntime = parseBooleanFlag(runtimeValue);
  if (parsedRuntime !== null) return parsedRuntime;

  const envValue = process.env[flagName];
  return parseBooleanFlag(envValue);
}

function shouldEmitBrowserDebugLogs(): boolean {
  if (!isBrowser) return false;
  const runtimeOverride = getBrowserRuntimeFlag('NEXT_PUBLIC_ENABLE_CLIENT_DEBUG_LOGS');
  if (runtimeOverride !== null) return runtimeOverride;
  return !isProductionBuild;
}

function shouldShipBrowserInfoLogs(): boolean {
  if (!isBrowser) return false;
  const runtimeOverride = getBrowserRuntimeFlag('NEXT_PUBLIC_ENABLE_CLIENT_INFO_LOGS');
  if (runtimeOverride !== null) return runtimeOverride;
  return !isProductionBuild;
}

// ============================================================================
// RATE LIMITING & CIRCUIT BREAKER
// Prevents ERR_INSUFFICIENT_RESOURCES and infinite retry loops
// ============================================================================

// Rate limiting: max logs per window
const RATE_LIMIT_WINDOW_MS = 1000; // 1 second window
const RATE_LIMIT_MAX_LOGS = 10; // max 10 logs per second
let rateLimitCount = 0;
let rateLimitWindowStart = 0;

// Circuit breaker: stop sending if too many failures
const CIRCUIT_BREAKER_THRESHOLD = 5; // failures before opening circuit
const CIRCUIT_BREAKER_RESET_MS = 30000; // 30 seconds before retry
let circuitBreakerFailures = 0;
let circuitBreakerOpenTime = 0;

// Auth error suppression: suppress ALL server logging during auth error state
// This breaks the cascade: auth error -> log calls -> fetch requests -> more errors
const AUTH_ERROR_SUPPRESSION_MS = 30000; // 30 seconds of suppression after auth error
let authErrorSuppressionStart = 0;

/**
 * Enter auth error suppression mode.
 * Called when auth errors are detected to prevent render cascade.
 * During suppression, logs still go to console but NOT to the server.
 */
export function enterAuthErrorSuppression(): void {
  authErrorSuppressionStart = Date.now();
  if (isBrowser) {
    console.warn('[Logger] Auth error suppression activated for 30s - server logging paused');
  }
}

function isAuthErrorSuppressed(): boolean {
  if (authErrorSuppressionStart === 0) return false;
  if (Date.now() - authErrorSuppressionStart > AUTH_ERROR_SUPPRESSION_MS) {
    authErrorSuppressionStart = 0; // Reset suppression
    return false;
  }
  return true;
}

function isRateLimited(): boolean {
  const now = Date.now();

  // Reset window if expired
  if (now - rateLimitWindowStart > RATE_LIMIT_WINDOW_MS) {
    rateLimitCount = 0;
    rateLimitWindowStart = now;
  }

  // Check if over limit
  if (rateLimitCount >= RATE_LIMIT_MAX_LOGS) {
    return true;
  }

  rateLimitCount++;
  return false;
}

function isCircuitOpen(): boolean {
  if (circuitBreakerFailures < CIRCUIT_BREAKER_THRESHOLD) {
    return false;
  }

  // Check if we should try again (circuit half-open)
  const now = Date.now();
  if (now - circuitBreakerOpenTime > CIRCUIT_BREAKER_RESET_MS) {
    // Reset circuit breaker - try again
    circuitBreakerFailures = 0;
    return false;
  }

  return true; // Circuit still open
}

function recordSuccess(): void {
  circuitBreakerFailures = 0;
}

function recordFailure(): void {
  circuitBreakerFailures++;
  if (circuitBreakerFailures >= CIRCUIT_BREAKER_THRESHOLD) {
    circuitBreakerOpenTime = Date.now();
    // Log to console only (not to server) when circuit opens
    console.warn('[Logger] Circuit breaker opened - pausing server logging for 30s');
  }
}

const getConsoleLevel = (type: string): 'log' | 'info' | 'warn' | 'error' | 'debug' => {
  switch (type) {
    case 'error':
      return 'error';
    case 'warn':
      return 'warn';
    case 'debug':
      return 'debug';
    case 'info':
      return 'info';
    default:
      return 'log';
  }
};

const logToConsole = (level: 'log' | 'info' | 'warn' | 'error' | 'debug', component: string, message: string, meta?: any) => {
  const prefix = component ? `[${component}]` : '';
  if (meta !== undefined) {
    console[level](`${prefix} ${message}`, meta);
    return;
  }
  console[level](`${prefix} ${message}`);
};

const logger = {
  // Generic log method (used by old code)
  log: (type: string, component: string, data: any, message?: string) => {
    if (isBrowser) {
      if (type === 'debug' && !shouldEmitBrowserDebugLogs()) {
        return;
      }
      if (type === 'info' && !shouldShipBrowserInfoLogs()) {
        if (shouldEmitBrowserDebugLogs()) {
          logToConsole('info', component, message || type, data);
        }
        return;
      }
      sendToServer(type, component, message || type, data);
      return;
    }
    logToConsole(getConsoleLevel(type), component, message || type, data);
  },

  error: (component: string, message: string, meta?: any) => {
    if (isBrowser) {
      sendToServer('error', component, message, meta);
      return;
    }
    logToConsole('error', component, message, meta);
  },

  warn: (component: string, message: string, meta?: any) => {
    if (isBrowser) {
      sendToServer('warn', component, message, meta);
      return;
    }
    logToConsole('warn', component, message, meta);
  },

  info: (component: string, message: string, meta?: any) => {
    if (isBrowser) {
      if (!shouldShipBrowserInfoLogs()) {
        if (shouldEmitBrowserDebugLogs()) {
          logToConsole('info', component, message, meta);
        }
        return;
      }
      sendToServer('info', component, message, meta);
      return;
    }
    logToConsole('info', component, message, meta);
  },

  debug: (component: string, message: string, meta?: any) => {
    // DEBUG LOGS: Console-only, never send to server
    // Sending debug logs to server caused ERR_INSUFFICIENT_RESOURCES when
    // auth state changes triggered 10+ debug logs in rapid succession.
    // Debug logs are for development - no need to persist them server-side.
    if (isBrowser && !shouldEmitBrowserDebugLogs()) {
      return;
    }
    logToConsole('debug', component, `[DEBUG] ${message}`, meta);
  },

  aiAnalysis: (component: string, data: any) => {
    if (isBrowser) {
      sendToServer('ai_analysis_received', component, 'AI Analysis Data', data);
      return;
    }
    logToConsole('info', component, 'AI Analysis Data', data);
  },

  suggestedActions: (component: string, actions: any[]) => {
    const payload = {
      count: actions.length,
      actions,
    };
    if (isBrowser) {
      sendToServer('suggested_actions_rendered', component, 'Suggested Actions', payload);
      return;
    }
    logToConsole('info', component, 'Suggested Actions', payload);
  },

  transcriptMonitor: (message: string, meta?: any) => {
    // TRANSCRIPT_MONITOR: Console-only, never send to server.
    // This fires 50+ times/sec during active transcription (every React state change),
    // which exhausts browser sendBeacon quota causing ERR_INSUFFICIENT_RESOURCES.
    // Uses 'debug' level so these don't appear in browser console by default or
    // clutter Playwright info-level captures. Enable verbose/debug in devtools to see them.
    logToConsole('debug', 'TranscriptMonitor', `[TRANSCRIPT_MONITOR] ${message}`, meta);
  },
};

// ============================================================================
// LOG BATCHING
// Accumulates logs and flushes as a single request every 2s (or 20 items).
// Reduces ~600 individual sendBeacon calls/min to ~30 batched requests/min.
// ============================================================================

const LOG_FLUSH_INTERVAL_MS = 2000; // Flush every 2 seconds
const LOG_BUFFER_MAX_SIZE = 20; // Flush when buffer hits 20 items

// Buffer stores pre-serialized JSON strings, NOT objects.
// Meta data often contains Redux/Immer proxies that get revoked before the
// 2s flush fires. Serializing eagerly at push time avoids "revoked proxy" errors.
let logBuffer: string[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let flushListenerRegistered = false;

/**
 * Flush all buffered logs to the server in a single request.
 * Uses sendBeacon (preferred) or fetch (fallback).
 */
function flushBuffer() {
  if (logBuffer.length === 0) return;

  // Grab current buffer and reset
  const batch = logBuffer;
  logBuffer = [];

  // Clear any pending timer since we're flushing now
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }

  // /api/log refuses a body over LOG_ENDPOINT_MAX_BODY_BYTES with a 413, which sendBeacon never reports. So a
  // batch is shipped in payloads that fit (a flush holds at most LOG_BUFFER_MAX_SIZE = 20 entries, under the
  // endpoint's 25-entry cap), and an entry too large for one body is SPLIT into ordered parts, never clipped
  // or dropped (owner rule: no silent content loss).
  const entries: string[] = [];
  for (const entry of batch) {
    if (utf8Bytes(entry) + 2 > LOG_ENDPOINT_MAX_BODY_BYTES) entries.push(...logPartEntries(entry));
    else entries.push(entry);
  }
  for (const payload of payloadsWithinEndpointLimit(entries)) shipPayload(payload);
}

const utf8 = typeof TextEncoder !== 'undefined' ? new TextEncoder() : null;
function utf8Bytes(text: string): number {
  return utf8 ? utf8.encode(text).length : text.length * 3;
}

/**
 * An entry too large for one /api/log body, as ordered part entries that each fit one. Every part keeps the
 * entry's `type` (so an error stays an error in the store) and carries `data: { entryId, part: { index, count },
 * payload }`: the entry's own JSON text, cut into pieces. To read it back, take the parts with its entryId, join
 * their payloads in part.index order and JSON.parse the result (the same shape lib/textTrace.ts uses).
 * Each part's size is MEASURED, not estimated: a piece that would not fit is halved until it does.
 */
function logPartEntries(serialized: string): string[] {
  let type = 'error';
  try {
    const parsed = JSON.parse(serialized) as { type?: unknown };
    if (typeof parsed.type === 'string') type = parsed.type;
  } catch { /* keep 'error': the entry text itself still travels whole in the parts */ }
  const entryId = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  const part = (index: number, count: number, payload: string) => JSON.stringify({
    type,
    component: 'LOGGER-PART',
    message: `[LOG-PART ${index}/${count}] ${entryId}`,
    timestamp: new Date().toISOString(),
    tabId: TAB_ID,
    data: { entryId, part: { index, count }, payload },
  });
  const budget = LOG_ENDPOINT_MAX_BODY_BYTES - 2; // the payload's own '[' and ']'
  const envelope = utf8Bytes(part(99_999, 99_999, ''));
  const fits = (piece: string) => envelope + utf8Bytes(JSON.stringify(piece)) - 2 <= budget;
  // A first cut from the entry's average escaping, then every piece is checked and halved until it fits.
  const escapedPerChar = Math.max(1, utf8Bytes(JSON.stringify(serialized)) / Math.max(1, serialized.length));
  const firstCut = Math.max(1, Math.floor((budget - envelope) / (escapedPerChar * 1.25)));
  const pieces: string[] = [];
  const place = (piece: string): void => {
    if (piece.length <= 1 || fits(piece)) {
      pieces.push(piece);
      return;
    }
    const middle = Math.floor(piece.length / 2);
    place(piece.slice(0, middle));
    place(piece.slice(middle));
  };
  for (let start = 0; start < serialized.length; start += firstCut) place(serialized.slice(start, start + firstCut));
  return pieces.map((piece, index) => part(index + 1, pieces.length, piece));
}

/** Group pre-serialized entries (each within the limit) into JSON-array payloads of at most the limit (UTF-8). */
function payloadsWithinEndpointLimit(entries: string[]): string[] {
  const payloads: string[] = [];
  let current: string[] = [];
  let currentBytes = 2; // '[' and ']'
  for (const entry of entries) {
    const bytes = utf8Bytes(entry);
    const added = bytes + (current.length > 0 ? 1 : 0); // the ',' separator
    if (current.length > 0 && currentBytes + added > LOG_ENDPOINT_MAX_BODY_BYTES) {
      payloads.push('[' + current.join(',') + ']');
      current = [];
      currentBytes = 2;
    }
    currentBytes += bytes + (current.length > 0 ? 1 : 0);
    current.push(entry);
  }
  if (current.length > 0) payloads.push('[' + current.join(',') + ']');
  return payloads;
}

function shipPayload(payload: string): void {
  // Every payload fits under the endpoint limit, which is below sendBeacon's 64 KB.
  if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
    // ⚠️ sendBeacon THROWS (not returns false) when the page cannot resolve the
    // relative URL — measured 2026-09-11 on a bundle page at about:blank. The
    // logger is the failsafe's own voice: it must never throw into its caller
    // or out of its flush timer. Contained, counted, and said at ERROR.
    try {
      const blob = new Blob([payload], { type: 'application/json' });
      if (navigator.sendBeacon('/api/log', blob)) {
        recordSuccess();
        return;
      }
    } catch (error) {
      reportShippingFailure('sendBeacon', error);
    }
    recordFailure();
    // Fall through to fetch
  }

  // Fallback to fetch. Only a 2xx stored the batch: a 413, 429 or 5xx is a lost batch, said and counted.
  try {
    fetch('/api/log', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: payload,
      keepalive: true,
    })
      .then((response) => {
        if (response.ok) {
          recordSuccess();
          return;
        }
        reportShippingFailure('fetch', new Error(`/api/log answered HTTP ${response.status}`));
        recordFailure();
      })
      .catch((error: unknown) => {
        reportShippingFailure('fetch', error);
        recordFailure();
      });
  } catch (error) {
    // A synchronous throw (an unresolvable URL) — the batch is lost, never the page.
    reportShippingFailure('fetch', error);
    recordFailure();
  }
}

/** The logger cannot ship its own failure; the console is the only place left. */
function reportShippingFailure(channel: 'sendBeacon' | 'fetch', error: unknown): void {
  console.error(
    `[Logger] log shipping failed via ${channel}; the batch stays on this console`,
    error instanceof Error ? error.message : String(error),
  );
}

function scheduleFlush() {
  if (flushTimer) return; // Already scheduled
  flushTimer = setTimeout(flushBuffer, LOG_FLUSH_INTERVAL_MS);
}

function registerFlushOnUnload() {
  if (flushListenerRegistered || !isBrowser) return;
  flushListenerRegistered = true;
  window.addEventListener('beforeunload', flushBuffer);
}

/**
 * Send log to server via batched buffer.
 *
 * Logs are accumulated and flushed as a single request every 2s
 * (or when buffer hits 20 items, or on page unload).
 *
 * Includes rate limiting and circuit breaker to prevent:
 * - ERR_INSUFFICIENT_RESOURCES from too many requests
 * - Infinite retry loops when network is failing
 */
function sendToServer(type: string, component: string, message: string, meta?: any) {
  if (!isBrowser) return;

  // Auth error suppression: during auth errors, log to console only
  if (isAuthErrorSuppressed()) {
    logToConsole(getConsoleLevel(type), component, message, meta);
    return;
  }

  // Circuit breaker: stop sending if recent failures
  if (isCircuitOpen()) {
    logToConsole(getConsoleLevel(type), component, message, meta);
    return;
  }

  // Rate limiting: prevent flooding
  if (isRateLimited()) {
    return;
  }

  // Register beforeunload listener on first use
  registerFlushOnUnload();

  // Serialize immediately — meta may contain Redux/Immer proxies that get
  // revoked before the deferred flush. Storing the JSON string is safe.
  // Try-catch: proxies can be mid-revocation during a synchronous dispatch cycle.
  let serialized: string;
  try {
    serialized = JSON.stringify({
      type,
      component,
      message,
      timestamp: new Date().toISOString(),
      tabId: TAB_ID,
      data: meta,
    });
  } catch {
    // Proxy revoked or circular ref — serialize without meta
    serialized = JSON.stringify({
      type,
      component,
      message,
      timestamp: new Date().toISOString(),
      tabId: TAB_ID,
      data: { _serializationError: true },
    });
  }
  logBuffer.push(serialized);

  // Flush if buffer is full, otherwise schedule a timed flush
  if (logBuffer.length >= LOG_BUFFER_MAX_SIZE) {
    flushBuffer();
  } else {
    scheduleFlush();
  }
}

/**
 * Ship the buffered logs now. For an ERROR raised while the page is going away
 * (a pagehide handler runs after this module's own unload flush), so it is not
 * left in a buffer that dies with the page.
 */
export function flushLogsNow(): void {
  flushBuffer();
}

export default logger;
